/**
 * Pure helpers for the MIPS Overview (P5).
 * (docs/reference/app-plus/IMPLEMENTATION_PLAN.md, P5)
 */

export interface PortfolioAlert {
  practiceId: number;
  kind: 'stale_upload' | 'low_dc' | 'below_case_min' | 'no_data';
  label: string;
}

const STALE_UPLOAD_DAYS = 45;

/**
 * Upload-recency alert. Malformed/missing dates report as no_data — the card
 * must degrade to "no uploads yet", never crash the portfolio.
 */
export function computeUploadAlert(
  lastUploadAt: string | null,
  now: Date
): 'stale_upload' | 'no_data' | null {
  if (!lastUploadAt) return 'no_data';
  const t = Date.parse(lastUploadAt);
  if (Number.isNaN(t)) return 'no_data';
  const days = (now.getTime() - t) / 86_400_000;
  return days > STALE_UPLOAD_DAYS ? 'stale_upload' : null;
}

/**
 * Rank by (potential − current) points descending; ties stable-sorted by
 * measureId (natural numeric order) so re-renders don't reshuffle.
 */
export function topOpportunities<T extends {
  measureId: string; currentPoints: number; potentialPoints: number;
}>(rows: T[], n: number): T[] {
  return [...rows]
    .sort((a, b) => {
      const delta = (b.potentialPoints - b.currentPoints) - (a.potentialPoints - a.currentPoints);
      if (delta !== 0) return delta;
      return a.measureId.localeCompare(b.measureId, undefined, { numeric: true, sensitivity: 'base' });
    })
    .slice(0, Math.max(0, n));
}

/** "3 days ago" / "today" label for upload recency. Fails soft to '—'. */
export function daysAgoLabel(dateStr: string | null, now: Date): string {
  if (!dateStr) return '—';
  const t = Date.parse(dateStr);
  if (Number.isNaN(t)) return '—';
  const days = Math.floor((now.getTime() - t) / 86_400_000);
  if (days <= 0) return 'today';
  if (days === 1) return 'yesterday';
  return `${days} days ago`;
}
